import React from 'react'
import { Select, Tag } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { get } from 'lodash'
import { getReq } from '../utils/request'

const ROLE_TAG: Record<string, { color: string; label: string }> = {
	ADMIN: { color: 'blue', label: 'Admin' },
	SUPERADMIN: { color: 'geekblue', label: 'Superadmin' },
	PHONE_OPERATOR: { color: 'purple', label: 'Telefonistka' },
	USER: { color: 'default', label: 'Pracovník' }
}

type Props = {
	queryParams?: Record<string, unknown>
	multiple?: boolean
	placeholder?: string
	value?: (number | null)[]
	onChange?: (values: number[]) => void
	style?: React.CSSProperties
	allowClear?: boolean
}

const SelectUsersQueryField = ({
	queryParams = {},
	multiple,
	placeholder = 'Vyber osobu',
	value,
	onChange,
	style,
	allowClear = true
}: Props) => {
	const { data: users = [], isLoading } = useQuery({
		queryKey: ['selectUsers', queryParams],
		queryFn: async () => {
			const { data } = await getReq('/api/admin/users', { page: 1, limit: 500, ...queryParams })
			const raw = get(data, 'users', []) as Array<{ id: number; firstName: string; lastName: string; email: string; role: string }>
			const ROLE_ORDER: Record<string, number> = { PHONE_OPERATOR: 0, ADMIN: 1, SUPERADMIN: 2, USER: 3 }
			return raw
				.map((u) => ({
					value: u.id,
					label: u.lastName ? `${u.lastName} ${u.firstName}` : u.email,
					role: u.role
				}))
				.sort((a, b) => {
					const roleSort = (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99)
					return roleSort !== 0 ? roleSort : a.label.localeCompare(b.label)
				})
		},
		staleTime: 60_000
	})

	return (
		<Select
			mode={multiple ? 'multiple' : undefined}
			allowClear={allowClear}
			showSearch
			loading={isLoading}
			placeholder={placeholder}
			value={value as number[]}
			onChange={onChange}
			style={style ?? { minWidth: 200 }}
			filterOption={(input, option) => {
				const opt = users.find((u) => u.value === option?.value)
				return (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
			}}
		>
			{users.map((u) => {
				const tag = ROLE_TAG[u.role]
				return (
					<Select.Option key={u.value} value={u.value} label={u.label}>
						<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
							<span>{u.label}</span>
							{tag && (
								<Tag color={tag.color} style={{ marginLeft: 'auto', fontSize: 11 }}>
									{tag.label}
								</Tag>
							)}
						</span>
					</Select.Option>
				)
			})}
		</Select>
	)
}

export default SelectUsersQueryField
